"use client";
import { useState, useRef, useEffect, useCallback } from 'react';
import { WpMenu } from '@/lib/wp/types';
import { HeaderTranslations } from '@/lib/translations/types';

type Category = { id: string; title: string; href: string; icon?: string; links?: Category[] };
type NavItem = { id: string; title: string; href: string | undefined; categories?: Category[]; moreHref?: string; description?: string; linkTitle?: string };

// Remove the local TextContent type since we're using HeaderTranslations from the translation system

interface InteractiveHeaderProps {
  navItems: NavItem[];
  menu?: WpMenu;
  textContent: HeaderTranslations;
}

export default function HeaderClient({ navItems, textContent }: InteractiveHeaderProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMegaMenuHovered, setIsMegaMenuHovered] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState<string | null>(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollYRef = useRef(0);

  // Hydration-safe initialization
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Hide header on scroll down, show on scroll up; update reading progress
  useEffect(() => {
    if (!isHydrated) return;

    lastScrollYRef.current = window.scrollY;
    const scrollThreshold = 8;
    const progressEl = document.getElementById('ak-scroll-progress');

    const updateProgress = () => {
      if (!progressEl) return;

      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0
        ? Math.min(1, Math.max(0, window.scrollY / scrollable))
        : 0;

      progressEl.style.transform = `scaleX(${progress})`;
      progressEl.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    };

    const handleScroll = () => {
      updateProgress();

      // Keep header visible while mobile menu is open
      if (isMobileMenuOpen) {
        setIsHeaderHidden(false);
        lastScrollYRef.current = window.scrollY;
        return;
      }

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;

      if (currentScrollY <= 0) {
        setIsHeaderHidden(false);
      } else if (delta > scrollThreshold) {
        setIsHeaderHidden(true);
        lastScrollYRef.current = currentScrollY;
      } else if (delta < -scrollThreshold) {
        setIsHeaderHidden(false);
        lastScrollYRef.current = currentScrollY;
      }
    };

    updateProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateProgress);
    };
  }, [isHydrated, isMobileMenuOpen]);

  // Apply show/hide transform to the sticky header
  useEffect(() => {
    if (!isHydrated) return;

    const headerEl = document.querySelector('.ak-header');
    if (!headerEl) return;

    if (isHeaderHidden && !isMobileMenuOpen) {
      headerEl.classList.add('-translate-y-full');
      headerEl.classList.remove('translate-y-0');
    } else {
      headerEl.classList.remove('-translate-y-full');
      headerEl.classList.add('translate-y-0');
    }
  }, [isHydrated, isHeaderHidden, isMobileMenuOpen]);

  // Handle mouse enter on nav item
  const handleNavItemEnter = useCallback((idx: number) => {
    if (!isHydrated) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveIndex(idx);
  }, [isHydrated]);

  // Handle mouse leave from nav
  const handleNavLeave = useCallback(() => {
    if (!isHydrated) return;
    timeoutRef.current = setTimeout(() => {
      if (!isMegaMenuHovered) {
        setActiveIndex(null);
      }
    }, 150);
  }, [isHydrated, isMegaMenuHovered]);

  // Handle mouse enter on mega menu
  const handleMegaMenuEnter = useCallback(() => {
    if (!isHydrated) return;
    setIsMegaMenuHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [isHydrated]);

  // Handle mouse leave from mega menu
  const handleMegaMenuLeave = useCallback(() => {
    if (!isHydrated) return;
    setIsMegaMenuHovered(false);
    timeoutRef.current = setTimeout(() => {
      setActiveIndex(null);
    }, 150);
  }, [isHydrated]);

  // Handle mobile menu toggle
  const handleMobileMenuToggle = useCallback(() => {
    if (!isHydrated) return;
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
  }, [isHydrated, isMobileMenuOpen]);

  // Handle mobile menu link click (close menu)
  const handleMobileMenuLinkClick = useCallback(() => {
    if (!isHydrated) return;
    setIsMobileMenuOpen(false);
    setActiveMobileSubmenu(null);
  }, [isHydrated]);

  // Handle mobile nav item click
  const handleMobileNavItemClick = useCallback((itemId: string, hasCategories: boolean) => {
    if (!isHydrated) return;
    
    if (hasCategories) {
      // Show submenu
      setActiveMobileSubmenu(itemId);
    } else {
      // Navigate directly - this will be handled by the link
      setIsMobileMenuOpen(false);
    }
  }, [isHydrated]);

  // Handle mobile back button click
  const handleMobileBackClick = useCallback(() => {
    if (!isHydrated) return;
    setActiveMobileSubmenu(null);
  }, [isHydrated]);

  // Handle click outside mobile menu
  const handleClickOutside = useCallback((event: MouseEvent | TouchEvent) => {
    if (!isHydrated || !isMobileMenuOpen) return;
    
    const target = event.target as Element;
    const mobileMenu = document.getElementById('mobile-menu');
    const mobileSubmenus = document.querySelectorAll('[id^="mobile-submenu-"]');
    const mobileMenuButton = document.getElementById('mobile-menu-button');
    
    // Check if click is outside all mobile menu elements
    const isOutsideMenu = mobileMenu && !mobileMenu.contains(target);
    const isOutsideSubmenus = Array.from(mobileSubmenus).every(submenu => !submenu.contains(target));
    const isNotMenuButton = mobileMenuButton && !mobileMenuButton.contains(target);
    
    if (isOutsideMenu && isOutsideSubmenus && isNotMenuButton) {
      setIsMobileMenuOpen(false);
      setActiveMobileSubmenu(null);
    }
  }, [isHydrated, isMobileMenuOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Ensure scroll is unlocked on unmount
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  // Add interactivity to existing server-rendered elements
  useEffect(() => {
    if (!isHydrated) return;

    // Add hover handlers to desktop navigation
    const navItems = document.querySelectorAll('[data-nav-item]');
    navItems.forEach((item, idx) => {
      item.addEventListener('mouseenter', () => handleNavItemEnter(idx));
    });

    // Add mobile menu toggle handler
    const mobileMenuButton = document.getElementById('mobile-menu-button');
    if (mobileMenuButton) {
      mobileMenuButton.addEventListener('click', handleMobileMenuToggle);
    }

    // Add mobile nav item click handlers (only for buttons with submenus)
    const mobileNavItems = document.querySelectorAll('[data-mobile-nav-item]');
    mobileNavItems.forEach(item => {
      const itemId = item.getAttribute('data-mobile-nav-item');
      const hasCategories = item.getAttribute('aria-controls') !== null;
      
      item.addEventListener('click', () => {
        if (itemId) {
          handleMobileNavItemClick(itemId, hasCategories);
        }
      });
    });

    // Add mobile back button click handlers
    const mobileBackButtons = document.querySelectorAll('[data-mobile-back-button]');
    mobileBackButtons.forEach(button => {
      button.addEventListener('click', handleMobileBackClick);
    });

    // Add mobile menu link click handlers
    const mobileMenuLinks = document.querySelectorAll('#mobile-menu a, [id^="mobile-submenu-"] a');
    mobileMenuLinks.forEach(link => {
      link.addEventListener('click', handleMobileMenuLinkClick);
    });

    // Add mega menu hover handlers
    const megaMenus = document.querySelectorAll('[data-mega-menu]');
    megaMenus.forEach(menu => {
      menu.addEventListener('mouseenter', handleMegaMenuEnter);
      menu.addEventListener('mouseleave', handleMegaMenuLeave);
    });

    // Add nav leave handler
    const nav = document.querySelector('[data-main-nav]');
    if (nav) {
      nav.addEventListener('mouseleave', handleNavLeave);
    }

    // Add click outside handler
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      // Cleanup event listeners
      navItems.forEach((item, idx) => {
        item.removeEventListener('mouseenter', () => handleNavItemEnter(idx));
      });
      if (mobileMenuButton) {
        mobileMenuButton.removeEventListener('click', handleMobileMenuToggle);
      }
      mobileNavItems.forEach(item => {
        const itemId = item.getAttribute('data-mobile-nav-item');
        const hasCategories = item.getAttribute('aria-controls') !== null;
        item.removeEventListener('click', () => {
          if (itemId) {
            handleMobileNavItemClick(itemId, hasCategories);
          }
        });
      });
      mobileBackButtons.forEach(button => {
        button.removeEventListener('click', handleMobileBackClick);
      });
      mobileMenuLinks.forEach(link => {
        link.removeEventListener('click', handleMobileMenuLinkClick);
      });
      megaMenus.forEach(menu => {
        menu.removeEventListener('mouseenter', handleMegaMenuEnter);
        menu.removeEventListener('mouseleave', handleMegaMenuLeave);
      });
      if (nav) {
        nav.removeEventListener('mouseleave', handleNavLeave);
      }
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isHydrated, handleNavItemEnter, handleMobileMenuToggle, handleMobileMenuLinkClick, handleMobileNavItemClick, handleMobileBackClick, handleClickOutside, handleMegaMenuEnter, handleMegaMenuLeave, handleNavLeave]);

  // Update UI based on state
  useEffect(() => {
    if (!isHydrated) return;

    // Lock/unlock background scroll using body overflow only
    const headerEl = document.querySelector('.ak-header');
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Ensure header remains visible when body overflow is locked (sticky can break)
      if (headerEl) {
        headerEl.classList.add('fixed', 'top-0', 'left-0', 'right-0', 'z-50');
        headerEl.classList.remove('sticky');
      }
    } else {
      document.body.style.overflow = '';
      if (headerEl) {
        headerEl.classList.remove('fixed', 'left-0', 'right-0', 'z-50');
        headerEl.classList.add('sticky');
        // keep top-0 which exists originally
      }
    }

    // Update mobile menu visibility
    const mobileMenu = document.getElementById('mobile-menu');
    if (mobileMenu) {
      if (isMobileMenuOpen && !activeMobileSubmenu) {
        mobileMenu.classList.remove('hidden');
        mobileMenu.classList.add('block');
      } else {
        mobileMenu.classList.remove('block');
        mobileMenu.classList.add('hidden');
      }
    }

    // Update mobile submenu visibility
    navItems.forEach((item) => {
      const submenu = document.getElementById(`mobile-submenu-${item.id}`);
      if (submenu) {
        if (isMobileMenuOpen && activeMobileSubmenu === item.id) {
          submenu.classList.remove('hidden');
          submenu.classList.add('block');
        } else {
          submenu.classList.remove('block');
          submenu.classList.add('hidden');
        }
      }
    });

    // Update mobile menu button
    const mobileMenuButton = document.getElementById('mobile-menu-button');
    if (mobileMenuButton) {
      const svg = mobileMenuButton.querySelector('svg');
      if (svg) {
        // Clear existing content safely
        while (svg.firstChild) {
          svg.removeChild(svg.firstChild);
        }
        
        if (isMobileMenuOpen) {
          // Create close icon path safely
          const closePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          closePath.setAttribute('stroke-linecap', 'round');
          closePath.setAttribute('stroke-linejoin', 'round');
          closePath.setAttribute('stroke-width', '2');
          closePath.setAttribute('d', 'M6 18L18 6M6 6l12 12');
          svg.appendChild(closePath);
          mobileMenuButton.setAttribute('aria-label', textContent.closeMobileMenu);
          mobileMenuButton.setAttribute('aria-expanded', 'true');
        } else {
          // Create hamburger icon paths safely
          const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path1.setAttribute('stroke-linecap', 'round');
          path1.setAttribute('stroke-linejoin', 'round');
          path1.setAttribute('stroke-width', '2');
          path1.setAttribute('d', 'M4 6h16');
          svg.appendChild(path1);
          
          const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path2.setAttribute('stroke-linecap', 'round');
          path2.setAttribute('stroke-linejoin', 'round');
          path2.setAttribute('stroke-width', '2');
          path2.setAttribute('d', 'M4 12h16');
          svg.appendChild(path2);
          
          const path3 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path3.setAttribute('stroke-linecap', 'round');
          path3.setAttribute('stroke-linejoin', 'round');
          path3.setAttribute('stroke-width', '2');
          path3.setAttribute('d', 'M4 18h16');
          svg.appendChild(path3);
          
          mobileMenuButton.setAttribute('aria-label', textContent.openMobileMenu);
          mobileMenuButton.setAttribute('aria-expanded', 'false');
        }
      }
    }

    // Update mega menu visibility
    navItems.forEach((item, idx) => {
      const megaMenu = document.getElementById(`mega-menu-${item.id}`);
      if (megaMenu) {
        if (activeIndex === idx) {
          megaMenu.classList.remove('opacity-0', 'pointer-events-none');
          megaMenu.classList.add('opacity-100', 'pointer-events-auto');
        } else {
          megaMenu.classList.remove('opacity-100', 'pointer-events-auto');
          megaMenu.classList.add('opacity-0', 'pointer-events-none');
        }
      }

      // Update nav item active state
      const navItem = document.querySelector(`[data-nav-item="${idx}"]`);
      if (navItem) {
        const link = navItem.querySelector('a');
        if (link) {
          if (activeIndex === idx) {
            link.classList.add('text-blue-600');
            link.classList.remove('text-gray-800');
          } else {
            link.classList.remove('text-blue-600');
            link.classList.add('text-gray-800');
          }
          link.setAttribute('aria-expanded', activeIndex === idx ? 'true' : 'false');
        }
      }
    });
  }, [isHydrated, isMobileMenuOpen, activeMobileSubmenu, activeIndex, navItems, textContent.closeMobileMenu, textContent.openMobileMenu]);

  // This component only handles interactivity - no JSX return needed
  return null;
}
